import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import apiClient from "@/lib/api";

const POSITIONS = ["GK", "DEF", "MID", "FWD", "ST"];
const EMPTY_FORM = { name: "", position: "MID", age: 16, jersey_number: 0, notes: "" };

const positionColors = {
  GK: "bg-yellow-100 text-yellow-800",
  DEF: "bg-blue-100 text-blue-800",
  MID: "bg-green-100 text-green-800",
  FWD: "bg-orange-100 text-orange-800",
  ST: "bg-red-100 text-red-800",
};

export default function PlayersPage() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () =>
    apiClient
      .get("/players")
      .then((r) => setPlayers(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditingPlayer(null);
    setForm(EMPTY_FORM);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (player) => {
    setEditingPlayer(player);
    setForm({
      name: player.name,
      position: player.position,
      age: player.age,
      jersey_number: player.jersey_number,
      notes: player.notes || "",
    });
    setError("");
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingPlayer) {
        await apiClient.put(`/players/${editingPlayer.id}`, form);
      } else {
        await apiClient.post("/players", form);
      }
      setModalOpen(false);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this player from your squad?")) return;
    await apiClient.delete(`/players/${id}`).catch(console.error);
    load();
  };

  return (
    <div className="p-6 md:p-8" data-testid="players-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1
            className="text-3xl font-black text-gray-900"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Players
          </h1>
          <p className="text-gray-600 mt-1">
            {players.length} player{players.length !== 1 ? "s" : ""} in your squad
          </p>
        </div>
        <Button
          onClick={openAdd}
          className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
          data-testid="add-player-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Player
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : players.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-sm border border-gray-200">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3
            className="text-lg font-bold text-gray-900 mb-2"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            No players yet
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            Add your squad members to start tracking performance.
          </p>
          <Button
            onClick={openAdd}
            className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
            data-testid="add-first-player-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add First Player
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-gray-200 overflow-x-auto">
          <table className="w-full" data-testid="players-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">#</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Position</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Age</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">Notes</th>
                <th className="text-right px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {players.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-gray-50 transition-colors"
                  data-testid={`player-row-${p.id}`}
                >
                  <td className="px-6 py-4 text-sm font-bold text-gray-900">{p.jersey_number || "–"}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">{p.name}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`text-xs font-bold px-2 py-1 rounded-full ${
                        positionColors[p.position] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {p.position}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{p.age}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 hidden md:table-cell max-w-xs truncate">
                    {p.notes || "—"}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => openEdit(p)}
                      className="p-1.5 text-gray-400 hover:text-gray-700 mr-1 transition-colors"
                      data-testid={`edit-player-${p.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(p.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                      data-testid={`delete-player-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
              {editingPlayer ? "Edit Player" : "Add Player"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label htmlFor="player-name">Full Name *</Label>
              <Input
                id="player-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Player name"
                required
                className="mt-1"
                data-testid="player-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Position</Label>
                <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                  <SelectTrigger className="mt-1" data-testid="player-position-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POSITIONS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="player-jersey">Jersey #</Label>
                <Input
                  id="player-jersey"
                  type="number"
                  value={form.jersey_number}
                  onChange={(e) => setForm({ ...form, jersey_number: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                  data-testid="player-jersey-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="player-age">Age</Label>
              <Input
                id="player-age"
                type="number"
                value={form.age}
                onChange={(e) => setForm({ ...form, age: parseInt(e.target.value) || 16 })}
                className="mt-1"
                data-testid="player-age-input"
              />
            </div>
            <div>
              <Label htmlFor="player-notes">Notes</Label>
              <Input
                id="player-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional notes"
                className="mt-1"
                data-testid="player-notes-input"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2 pt-2">
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-sm"
                data-testid="save-player-btn"
              >
                {saving ? "Saving..." : editingPlayer ? "Update Player" : "Add Player"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setModalOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
